/**
 * Recepción Motor Taller — migración web de ares9010.
 *
 * Registra la entrada de un motor/vehículo al taller: se selecciona el
 * chasis desde el inventario, se describe el daño físico, el estado del
 * vehículo y el responsable. Al guardar, el backend abre la orden de taller
 * y se imprime el comprobante de entrada.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarProductos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// Comprobante de entrada a taller (impresión con iframe oculto).
function imprimirEntrada({ id, fecha, producto, danofisico, estadovehiculo, responsable }) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Entrada a Taller #${id}</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      <p style="text-align:center;margin:0 0 4px;font-weight:bold">Entrada a Taller</p>
      <p style="text-align:center;margin:0 0 14px;font-size:12px">Orden #${id}</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha", fecha)}
        ${fila("Chasis", producto?.chasis)}
        ${fila(
          "Vehículo",
          [producto?.marca, producto?.modelo, producto?.colore].filter(Boolean).join(" · ")
        )}
        ${fila("Daño físico", danofisico)}
        ${fila("Estado del vehículo", estadovehiculo)}
        ${fila("Responsable", responsable)}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function EntradaTaller({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [sel, setSel] = useState(null);
  const [danofisico, setDanofisico] = useState("");
  const [estadovehiculo, setEstadovehiculo] = useState("");
  const [responsable, setResponsable] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargarProductos = () => {
    setBuscando(true);
    post("/api/taller_chasis", {})
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.productos)) setOpts(data.productos);
        else if (!ok)
          showAlert({
            message: data.Error || "No se pudo cargar el inventario",
            color: "error",
          });
      })
      .finally(() => setBuscando(false));
  };

  useEffect(() => {
    cargarProductos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setSel(null);
    setDanofisico("");
    setEstadovehiculo("");
    setResponsable("");
  };

  const guardar = async () => {
    if (!sel?.chasis) {
      showAlert({ message: "Debe seleccionar el chasis", color: "warning" });
      return;
    }
    if (!estadovehiculo.trim()) {
      showAlert({ message: "El estado del vehículo es obligatorio", color: "warning" });
      return;
    }
    if (!responsable.trim()) {
      showAlert({ message: "El responsable es obligatorio", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/taller_entrada", {
        chasis: sel.chasis,
        danofisico: danofisico.trim(),
        estadovehiculo: estadovehiculo.trim(),
        responsable: responsable.trim(),
      });
      if (ok) {
        showAlert({
          message: data.mensajeok || `Entrada a taller registrada (orden #${data.id})`,
          color: "success",
        });
        imprimirEntrada({
          id: data.id,
          fecha: data.fecha,
          producto: data.producto || sel,
          danofisico: danofisico.trim(),
          estadovehiculo: estadovehiculo.trim(),
          responsable: responsable.trim(),
        });
        limpiar();
        if (onSaved) onSaved();
        cargarProductos();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la entrada", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Recepción Motor Taller"
        subtitle="Registra la entrada del vehículo al taller y abre la orden de reparación"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Vehículo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
            Vehículo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarProductos}
                options={opts}
                loading={buscando}
                value={sel}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : `${o.chasis} — ${[o.marca, o.modelo].filter(Boolean).join(" ")}`
                }
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                onChange={(_, v) => setSel(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.marca, o.modelo, o.colore].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Chasis, marca o modelo" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={7} sx={{ display: "flex", alignItems: "center" }}>
              {sel && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[sel.marca, sel.modelo, sel.colore].filter(Boolean).length > 0 && (
                    <Chip
                      label={[sel.marca, sel.modelo, sel.colore].filter(Boolean).join(" · ")}
                      sx={{
                        bgcolor: FP.color.surfaceAlt,
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {sel.estado && (
                    <Chip
                      label={`Estado: ${sel.estado}`}
                      sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
                    />
                  )}
                  {sel.condicion && (
                    <Chip
                      label={sel.condicion}
                      sx={{
                        bgcolor: FP.color.surfaceAlt,
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Datos de la recepción */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={BuildOutlinedIcon} color={FP.color.success}>
            Datos de la Recepción
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Daño físico"
                value={danofisico}
                onChange={(e) => setDanofisico(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Estado del vehículo"
                value={estadovehiculo}
                onChange={(e) => setEstadovehiculo(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Responsable"
                value={responsable}
                onChange={(e) => setResponsable(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardar}
                disabled={guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar entrada
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
